import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Bike,
  Calendar,
  CheckCircle2,
  ChevronRight,
  Clock,
  Filter,
  MapPin,
  Package,
  Phone,
  RefreshCw,
  Search,
  Store,
  Truck,
  User,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { API } from "@/lib/api";

const STATUS_LABELS = {
  new: "New",
  search: "Search",
  pickup_assignment_needed: "Pickup Assignment Needed",
  pickup_assigned: "Pickup Assigned",
  pickup_complete: "Pickup Complete",
  at_merchant: "At Merchant",
  in_service: "In Service",
  invoice_generated: "Invoice Generated",
  signature_pending: "Signature Pending",
  payout_pending: "Payout Pending",
  delivery_assignment_needed: "Delivery Assignment Needed",
  delivery_assigned: "Delivery Assigned",
  delivered: "Delivered",
  closed: "Closed",
};

const STATUS_COLOR = {
  new: "bg-slate-100 text-slate-700",
  search: "bg-violet-100 text-violet-700",
  pickup_assignment_needed: "bg-blue-50 text-blue-600",
  pickup_assigned: "bg-blue-100 text-blue-700",
  pickup_complete: "bg-blue-100 text-blue-700",
  at_merchant: "bg-amber-100 text-amber-700",
  in_service: "bg-amber-100 text-amber-700",
  invoice_generated: "bg-orange-100 text-orange-700",
  signature_pending: "bg-orange-100 text-orange-700",
  payout_pending: "bg-pink-100 text-pink-700",
  delivery_assignment_needed: "bg-cyan-50 text-cyan-600",
  delivery_assigned: "bg-cyan-100 text-cyan-700",
  delivered: "bg-emerald-100 text-emerald-700",
  closed: "bg-emerald-100 text-emerald-700",
};

const JOURNEY_LABELS = {
  home_to_shop: "Home → Shop → Home",
  shop_to_home: "Shop → Home",
  c2c: "C2C",
};

const JOURNEY_COLOR = {
  home_to_shop: "bg-blue-100 text-blue-700",
  shop_to_home: "bg-purple-100 text-purple-700",
  c2c: "bg-teal-100 text-teal-700",
};

const LEG_BADGE = {
  pickup: {
    label: "Pickup Leg",
    className: "bg-blue-100 text-blue-700 border-blue-200",
  },
  delivery: {
    label: "Delivery Leg",
    className: "bg-emerald-100 text-emerald-700 border-emerald-200",
  },
};

const PROOF_LABELS = {
  pickup_from_customer: "Pickup from Customer",
  drop_at_merchant: "Drop at Merchant",
  pickup_from_merchant: "Pickup from Merchant",
  delivered_to_customer: "Delivered to Customer",
};

const EVENT_ICONS = {
  status_change: Clock,
  rider_assigned: Bike,
  proof_uploaded: Package,
  invoice_linked: Store,
  payout: CheckCircle2,
  note: MapPin,
};

const EVENT_COLORS = {
  status_change: "text-blue-600 bg-blue-100",
  rider_assigned: "text-indigo-600 bg-indigo-100",
  proof_uploaded: "text-emerald-600 bg-emerald-100",
  invoice_linked: "text-orange-600 bg-orange-100",
  payout: "text-pink-600 bg-pink-100",
  note: "text-slate-600 bg-slate-100",
};

const isoDate = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const formatTimestamp = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const defaultFilters = () => {
  const now = new Date();
  const start = new Date();
  start.setDate(now.getDate() - 30);
  return {
    start_date: isoDate(start),
    end_date: isoDate(now),
    role: "all",
    journey_type: "all",
    status: "all",
    search: "",
    rider_id: "all",
  };
};

const FILTER_QUERY_KEYS = [
  "start_date",
  "end_date",
  "role",
  "journey_type",
  "status",
  "search",
];

// Map UI filter state → backend query string
const buildQuery = (filters, { includeRiderId = false } = {}) => {
  const p = new URLSearchParams();
  for (const key of FILTER_QUERY_KEYS) {
    const v = filters[key];
    if (!v) continue;
    if (["role", "journey_type", "status"].includes(key) && v === "all") continue;
    p.append(key, v);
  }
  if (includeRiderId && filters.rider_id && filters.rider_id !== "all") {
    p.append("rider_id", filters.rider_id);
  }
  return p.toString();
};

const TripDetail = ({ riderId, orderId, leg, stops, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const qs = leg ? `?leg=${encodeURIComponent(leg)}` : "";
        const res = await fetch(
          `${API}/riders/${riderId}/trips/${orderId}${qs}`,
        );
        if (!res.ok) {
          let msg = "Failed to load trip log";
          try {
            const d = await res.json();
            msg = d.detail || msg;
          } catch {}
          throw new Error(msg);
        }
        const json = await res.json();
        if (active) setData(json);
      } catch (e) {
        toast.error(e.message || "Failed to load trip log");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [riderId, orderId, leg]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-slate-400">
        <span className="w-6 h-6 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin mr-2" />
        Loading trip log…
      </div>
    );
  }
  if (!data) {
    return (
      <div className="py-6 text-sm text-slate-500 text-center">
        Couldn't load this trip log.
        {onClose && (
          <button onClick={onClose} className="ml-2 text-blue-600 underline">
            Close
          </button>
        )}
      </div>
    );
  }

  const { timeline, proofs } = data;

  // Prefer the authoritative stops computed by the backend (leg-scoped); fall
  // back to whatever the card already passed in so we never render an empty
  // detail if the leg info is missing for legacy orders.
  const legStops = (data.leg && data.leg.stops) || stops || [];
  const displayStops = legStops.filter(
    (s) => s && (s.time || s.location || s.items),
  );

  const STOP_TONE = [
    "bg-blue-50 border-blue-200 text-blue-800",
    "bg-emerald-50 border-emerald-200 text-emerald-800",
    "bg-amber-50 border-amber-200 text-amber-800",
    "bg-cyan-50 border-cyan-200 text-cyan-800",
  ];

  return (
    <div className="space-y-4 pt-2">
      {/* Leg stops */}
      {displayStops.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {displayStops.map((s, i) => (
            <div
              key={`${s.label}-${i}`}
              className={`rounded-xl border p-3 ${STOP_TONE[i % STOP_TONE.length]}`}
            >
              <p className="text-[10px] uppercase tracking-wide font-bold opacity-80">
                {s.label}
              </p>
              {s.time && (
                <p className="text-xs mt-1 flex items-center gap-1.5">
                  <Clock className="w-3 h-3" /> {formatTimestamp(s.time)}
                </p>
              )}
              {s.location && (
                <p className="text-xs mt-1 flex items-start gap-1.5">
                  <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
                  <span className="break-all">{s.location}</span>
                </p>
              )}
              {s.items && (
                <p className="text-xs mt-1 flex items-start gap-1.5">
                  <Package className="w-3 h-3 mt-0.5 shrink-0" />
                  <span>{s.items}</span>
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Timeline */}
      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="px-3 py-2 border-b border-slate-100">
          <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">
            Timeline ({timeline.length})
          </p>
        </div>
        <ol className="relative">
          {timeline.length === 0 && (
            <li className="px-3 py-4 text-xs text-slate-400">
              No timeline events yet.
            </li>
          )}
          {timeline.map((ev, idx) => {
            const Icon = EVENT_ICONS[ev.event_type] || Clock;
            const color =
              EVENT_COLORS[ev.event_type] || "text-slate-600 bg-slate-100";
            return (
              <li
                key={ev.id || idx}
                className="flex gap-3 px-3 py-2.5 border-b border-slate-50 last:border-0"
              >
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${color}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-800 break-words">
                    {ev.description}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    {formatTimestamp(ev.timestamp)}
                    {ev.actor && ev.actor !== "system" && (
                      <>
                        {" · "}
                        <span className="text-slate-600">{ev.actor}</span>
                      </>
                    )}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      {/* Proofs (leg-scoped when a leg is known) */}
      {proofs.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white">
          <div className="px-3 py-2 border-b border-slate-100">
            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">
              {leg ? "Leg Proof Photos" : "Proof Photos"} ({proofs.length})
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-3">
            {proofs.map((p) => (
              <a
                key={p.id}
                href={p.image_data || undefined}
                target="_blank"
                rel="noopener noreferrer"
                className="group rounded-lg overflow-hidden border border-slate-200 bg-slate-50"
              >
                {p.image_data ? (
                  <img
                    src={p.image_data}
                    alt={p.proof_type}
                    className="w-full aspect-square object-cover group-hover:opacity-90 transition-opacity"
                  />
                ) : (
                  <div className="w-full aspect-square flex items-center justify-center text-xs text-slate-400">
                    (no preview)
                  </div>
                )}
                <div className="px-2 py-1.5">
                  <p className="text-[10px] font-bold text-slate-700 truncate">
                    {PROOF_LABELS[p.proof_type] || p.proof_type}
                  </p>
                  <p className="text-[10px] text-slate-400">
                    {formatTimestamp(p.timestamp)}
                  </p>
                  {p.uploaded_by_name && (
                    <p className="text-[10px] text-slate-500 truncate">
                      by {p.uploaded_by_name}
                    </p>
                  )}
                </div>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const TripCard = ({ trip, expanded, onToggle, showRider }) => {
  const status = trip.status || "new";
  const jt = trip.journey_type || "home_to_shop";
  const leg = trip.leg;
  const legBadge = LEG_BADGE[leg] || {
    label: "Trip",
    className: "bg-slate-100 text-slate-700 border-slate-200",
  };
  const legTimestamp = trip.leg_timestamp || trip.updated_at;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 p-4 text-left active:bg-slate-50"
      >
        <div className="flex-1 min-w-0 space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wide border ${legBadge.className}`}
            >
              {legBadge.label}
            </span>
            <span className="text-[10px] font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
              {trip.order_number}
            </span>
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${JOURNEY_COLOR[jt] || "bg-slate-100 text-slate-600"}`}
            >
              {JOURNEY_LABELS[jt] || jt}
            </span>
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${STATUS_COLOR[status] || "bg-slate-100 text-slate-600"}`}
            >
              {STATUS_LABELS[status] || status}
            </span>
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                trip.leg_done
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-amber-100 text-amber-700"
              }`}
            >
              {trip.leg_done ? "Completed" : "In progress"}
            </span>
          </div>
          <p className="text-sm font-semibold text-slate-900 truncate">
            {trip.leg_label || "Trip"}
          </p>
          <p className="text-xs text-slate-500 truncate">
            {trip.customer_name || "—"}
          </p>
          <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
            {showRider && trip.leg_rider_name && (
              <span className="flex items-center gap-1">
                <User className="w-3 h-3" /> {trip.leg_rider_name}
              </span>
            )}
            {trip.merchant_name && (
              <span className="flex items-center gap-1">
                <Store className="w-3 h-3" /> {trip.merchant_name}
              </span>
            )}
            {trip.customer_phone && (
              <span className="flex items-center gap-1">
                <Phone className="w-3 h-3" /> {trip.customer_phone}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" /> {formatDate(legTimestamp)}
            </span>
          </div>
        </div>
        <ChevronRight
          className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${expanded ? "rotate-90" : ""}`}
        />
      </button>
      {expanded && (
        <div className="border-t border-slate-100 px-4 pb-4">
          <TripDetail
            riderId={trip.leg_rider_id}
            orderId={trip.id}
            leg={trip.leg}
            stops={trip.leg_stops}
          />
        </div>
      )}
    </div>
  );
};

const FiltersBar = ({ filters, setFilters, riders, scope, onReset }) => {
  const [open, setOpen] = useState(false);
  const field = (key) => (v) => setFilters((f) => ({ ...f, [key]: v }));
  const fieldEv = (key) => (e) =>
    setFilters((f) => ({ ...f, [key]: e.target.value }));

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-3 space-y-3">
      {/* Top row: search + toggle */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            className="h-10 pl-9 bg-white"
            placeholder="Order #, customer, merchant, phone…"
            value={filters.search}
            onChange={fieldEv("search")}
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          className="h-10 gap-1.5"
          onClick={() => setOpen((v) => !v)}
        >
          <Filter className="w-3.5 h-3.5" />
          {open ? "Hide" : "Filters"}
        </Button>
      </div>

      {open && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {scope === "admin" && (
            <div className="space-y-1 lg:col-span-2">
              <Label className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">
                Rider
              </Label>
              <Select
                value={filters.rider_id}
                onValueChange={field("rider_id")}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="All riders" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All riders</SelectItem>
                  {riders.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.full_name}
                      {r.phone ? ` · ${r.phone}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-1">
            <Label className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">
              From
            </Label>
            <Input
              type="date"
              className="h-9"
              value={filters.start_date}
              onChange={fieldEv("start_date")}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">
              To
            </Label>
            <Input
              type="date"
              className="h-9"
              value={filters.end_date}
              onChange={fieldEv("end_date")}
            />
          </div>

          <div className="space-y-1">
            <Label className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">
              Role
            </Label>
            <Select value={filters.role} onValueChange={field("role")}>
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All roles</SelectItem>
                <SelectItem value="pickup">Pickup</SelectItem>
                <SelectItem value="delivery">Delivery</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">
              Journey
            </Label>
            <Select
              value={filters.journey_type}
              onValueChange={field("journey_type")}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All journeys</SelectItem>
                <SelectItem value="home_to_shop">Home → Shop → Home</SelectItem>
                <SelectItem value="shop_to_home">Shop → Home</SelectItem>
                <SelectItem value="c2c">C2C</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1 lg:col-span-2">
            <Label className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">
              Status
            </Label>
            <Select value={filters.status} onValueChange={field("status")}>
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>
                    {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="sm:col-span-2 lg:col-span-4 flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              className="text-slate-500 gap-1.5"
              onClick={onReset}
            >
              <X className="w-3.5 h-3.5" /> Reset filters
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const EmptyState = ({ onReset }) => (
  <div className="flex flex-col items-center justify-center py-20 gap-3 text-slate-400">
    <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center">
      <Truck className="w-7 h-7" />
    </div>
    <p className="text-sm font-semibold text-slate-600">No trips match your filters</p>
    <button onClick={onReset} className="text-xs text-blue-600 font-medium">
      Reset filters
    </button>
  </div>
);

const LoadingState = () => (
  <div className="flex flex-col items-center justify-center py-20 gap-3 text-slate-400">
    <span className="w-8 h-8 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
    <p className="text-sm">Loading trips…</p>
  </div>
);

export const RiderTripLogs = ({ scope = "admin" }) => {
  const navigate = useNavigate();
  const [rider, setRider] = useState(null);
  const [riders, setRiders] = useState([]);
  const [filters, setFilters] = useState(defaultFilters);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const fetchSeq = useRef(0);

  // Rider scope: pull logged-in rider from localStorage. Admin scope: load riders list.
  useEffect(() => {
    if (scope === "rider") {
      const stored = localStorage.getItem("pokus_rider");
      if (!stored) {
        navigate("/rider");
        return;
      }
      try {
        setRider(JSON.parse(stored));
      } catch {
        navigate("/rider");
      }
    } else {
      (async () => {
        try {
          const res = await fetch(`${API}/riders`);
          if (res.ok) setRiders(await res.json());
        } catch {
          /* non-fatal */
        }
      })();
    }
  }, [scope, navigate]);

  const effectiveRiderId = scope === "rider" ? rider?.id : null;

  const fetchTrips = useCallback(
    async ({ silent = false } = {}) => {
      if (scope === "rider" && !effectiveRiderId) return;
      const seq = ++fetchSeq.current;
      if (silent) setRefreshing(true);
      else setLoading(true);
      try {
        const qs = buildQuery(filters, { includeRiderId: scope === "admin" });
        const url =
          scope === "rider"
            ? `${API}/riders/${effectiveRiderId}/trips${qs ? `?${qs}` : ""}`
            : `${API}/riders/trips${qs ? `?${qs}` : ""}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error("Failed to load trip logs");
        const data = await res.json();
        if (seq === fetchSeq.current) setTrips(data);
      } catch (e) {
        if (!silent) toast.error(e.message || "Failed to load trip logs");
      } finally {
        if (seq === fetchSeq.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [filters, scope, effectiveRiderId],
  );

  useEffect(() => {
    if (scope === "rider" && !effectiveRiderId) return;
    fetchTrips();
  }, [fetchTrips, scope, effectiveRiderId]);

  const handleReset = () => {
    setFilters(defaultFilters());
    setExpandedId(null);
  };

  const selectedRiderName = useMemo(() => {
    if (scope !== "admin") return null;
    if (!filters.rider_id || filters.rider_id === "all") return "All riders";
    const r = riders.find((x) => x.id === filters.rider_id);
    return r?.full_name || "Selected rider";
  }, [filters.rider_id, riders, scope]);

  const content = (
    <div className="space-y-4">
      <FiltersBar
        filters={filters}
        setFilters={setFilters}
        riders={riders}
        scope={scope}
        onReset={handleReset}
      />

      {loading ? (
        <LoadingState />
      ) : trips.length === 0 ? (
        <EmptyState onReset={handleReset} />
      ) : (
        <div className="space-y-2.5">
          <div className="flex items-center justify-between px-1">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">
              {trips.length} {trips.length === 1 ? "trip" : "trips"}
              {scope === "admin" && selectedRiderName ? ` · ${selectedRiderName}` : ""}
            </p>
            <button
              onClick={() => fetchTrips({ silent: true })}
              disabled={refreshing}
              className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
            >
              <RefreshCw
                className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`}
              />
              Refresh
            </button>
          </div>
          {trips.map((t) => (
            <TripCard
              key={t.trip_id || `${t.id}::${t.leg}`}
              trip={t}
              showRider={scope === "admin"}
              expanded={expandedId === (t.trip_id || t.id)}
              onToggle={() => {
                const key = t.trip_id || t.id;
                setExpandedId((id) => (id === key ? null : key));
              }}
            />
          ))}
        </div>
      )}
    </div>
  );

  if (scope === "rider") {
    if (!rider) return null;
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
          <div className="flex items-center justify-between px-4 py-3.5 max-w-2xl mx-auto">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/rider/dashboard")}
                className="p-2 -ml-2 rounded-xl text-slate-500 active:bg-slate-100"
                aria-label="Back"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <p className="text-sm font-bold text-slate-900 leading-tight">
                  Trip Logs
                </p>
                <p className="text-xs text-slate-500">
                  {rider.full_name} · {rider.phone}
                </p>
              </div>
            </div>
            <button
              onClick={() => fetchTrips({ silent: true })}
              disabled={refreshing}
              className="p-2 rounded-xl text-slate-500 active:bg-slate-100"
            >
              <RefreshCw
                className={`w-4.5 h-4.5 ${refreshing ? "animate-spin" : ""}`}
              />
            </button>
          </div>
        </div>
        <div className="max-w-2xl mx-auto px-4 py-5">{content}</div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
            Rider Trip Logs
          </h1>
          <p className="text-sm text-slate-500">
            Browse completed and in-flight trips by any rider with full
            timeline and proof history.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchTrips({ silent: true })}
          disabled={refreshing}
          className="gap-1.5"
        >
          <RefreshCw
            className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`}
          />
          Refresh
        </Button>
      </div>
      {content}
    </div>
  );
};

export default RiderTripLogs;
